import {
  bboxFromCenter,
  decodePolyline,
  EMPTY_CAMERA_SET,
  fetchCamerasForBbox,
  fetchSpeedLimitWays,
  haversineKm,
  initials,
  matchSpeedLimit,
  mergeCameraSets,
  rampExpression,
  trackToSegments,
  type CameraSet,
  type SpeedLimitWay,
} from '@motogo/shared';
import type { Feature, FeatureCollection } from 'geojson';
import maplibregl, { type GeoJSONSource } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useCallback, useEffect, useRef, useState, type FC } from 'react';
import { useNavigate } from 'react-router-dom';

import BottomBar from './BottomBar';
import SettingsModal from './SettingsModal';
import TelemetryDrawer from './TelemetryDrawer';

import { useDiscover } from '@/hooks/useDiscover';
import { useGps } from '@/hooks/useGps';
import { useRideRecorder } from '@/hooks/useRideRecorder';
import { useRideReview } from '@/hooks/useRideReview';
import { useRouteEditor } from '@/hooks/useRouteEditor';
import { useRoutePlan } from '@/hooks/useRoutePlan';
import {
  buildProgressChunks,
  cumulativeMeters,
  extractRouteLine,
  useRouteProgress,
} from '@/hooks/useRouteProgress';
import { useSettings, zoomBaseFromOffset } from '@/hooks/useSettings';
import { useVoice } from '@/hooks/useVoice';
import { useWakeLock } from '@/hooks/useWakeLock';
import { boundsFromGeoJSON } from '@/lib/map';
import {
  addLayersToMap,
  DISCOVER_ROUTES_LAYER_ID,
  EDITOR_LINE_LAYER_IDS,
  EDITOR_WAYPOINT_LAYER_ID,
  peerColorIndex,
  PROGRESS_AHEAD_COLOR,
  PROGRESS_RIDDEN_COLOR,
  ROUTE_LAYER_IDS,
  ROUTE_PROGRESS_LAYER_IDS,
  SOURCES,
} from '@/lib/mapLayers';

const MAP_STYLE = 'https://tiles.openfreemap.org/styles/liberty';

// Remember roughly where the rider was, so a cold start opens on their area instead of
// [0,0] (the Pacific) while the first GPS fix is still resolving.
const LAST_POS_KEY = 'motogo.lastpos';
const LAST_POS_SAVE_MS = 15_000;

function readLastPos(): { center: [number, number]; zoom: number } {
  try {
    const raw = localStorage.getItem(LAST_POS_KEY);
    if (raw) {
      const { lat, lon } = JSON.parse(raw) as { lat: number; lon: number };
      if (Number.isFinite(lat) && Number.isFinite(lon)) return { center: [lon, lat], zoom: 13 };
    }
  } catch {
    /* corrupt / unavailable storage — fall through to the world view */
  }
  return { center: [0, 0], zoom: 2 };
}

// Speed-adaptive zoom: zoomBase at walking pace, decreases with speed.
// zoomBase comes from user settings (default 16.5, ±0.1 per tick over ±10 ticks).
const ZOOM_KPH_DIVISOR = 33;
const MOVING_KPH = 2;

// Phase 3 — Speed cameras + limits
const CAMERA_FETCH_KM = 10;
const CAMERA_REFRESH_KM = 8;
// Speed limits are prefetched as ways-with-geometry for a box around the rider (the
// cameras pattern, just tighter — urban way density makes big geometry boxes heavy) and
// matched locally on every GPS tick, so the display reacts to a road change immediately
// instead of after the next Overpass round-trip.
const SPEED_LIMIT_FETCH_KM = 1.2;
const SPEED_LIMIT_REFRESH_KM = 0.7;
const SPEED_LIMIT_SNAP_DEG = 0.005; // finer bbox grid than the cameras' 0.05° default
const SPEED_LIMIT_RETRY_MS = 15_000; // backoff after a failed fetch — retries are per tick

// Phase 5 — Group location sharing
// A rider whose last update is older than this is drawn greyed out (tunnel, dead zone,
// app backgrounded). 10s = several missed sends at the ~2.5s share cadence.
const PEER_STALE_MS = 10_000;
const PEER_STALE_SWEEP_MS = 3_000;

function targetZoom(kph: number, zoomBase: number, speedZoom: boolean) {
  return speedZoom && kph > MOVING_KPH ? zoomBase - kph / ZOOM_KPH_DIVISOR : zoomBase;
}

// The route-line gradient's endpoints (mapLayers). The editor draws per-segment features,
// which line-gradient can't span, so segments are split into chunks each coloured with the
// gradient sampled at its position along the route — same start-dark → end-light read.
const EDITOR_GRAD_START = [0x00, 0x00, 0xfa];
const EDITOR_GRAD_END = [0x66, 0xcc, 0xff];
// Enough chunks that the blend reads as a gradient, few enough that rebuilding the
// feature set on every edit stays trivial.
const EDITOR_GRAD_CHUNKS = 32;

function editorGradColor(t: number): string {
  const hex = EDITOR_GRAD_START.map((start, i) =>
    Math.round(start + (EDITOR_GRAD_END[i] - start) * Math.min(1, Math.max(0, t)))
      .toString(16)
      .padStart(2, '0'),
  );
  return `#${hex.join('')}`;
}

// Anchored route-progress emphasis: the vivid "you are here" window around the rider's
// progress, and the cadence at which the emphasis chases it. Repainting on 50 m steps
// (rather than every fix) keeps the setFilter/setPaintProperty churn negligible while
// staying visually continuous at riding speeds.
const PROGRESS_NEAR_BACK_M = 150;
const PROGRESS_NEAR_FWD_M = 1500;
const PROGRESS_STEP_M = 50;

// Review/editor/discover panels dock right on wide screens AND short-landscape phones;
// bottom sheets (planner) dock right only on short landscape (desktop keeps the centered
// bottom sheet). Both lists mirror the media queries in index.css — keep them in sync;
// fitBounds padding has to keep the geometry clear of whichever edge the panel owns.
const SHORT_LANDSCAPE_QUERY = '(orientation: landscape) and (max-height: 500px)';
const DOCKED_PANEL_QUERY = `(min-width: 820px), ${SHORT_LANDSCAPE_QUERY}`;

// Mirrors the docked panels' CSS width: min(44vw, 460px).
function dockedPanelWidth() {
  return Math.round(Math.min(window.innerWidth * 0.44, 460));
}

function panelAwarePadding(topMobile: number, bottomFraction: number) {
  if (window.matchMedia(DOCKED_PANEL_QUERY).matches) {
    return { top: 90, right: dockedPanelWidth() + 40, bottom: 40, left: 40 };
  }
  return { top: topMobile, right: 40, bottom: Math.round(window.innerHeight * bottomFraction) + 20, left: 40 };
}

const MapView: FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  // trackingRef: read by map event handlers (avoids stale closure)
  // tracking state: drives button visibility in the UI
  const trackingRef = useRef(true);
  const [tracking, setTracking] = useState(true);
  const [showNorthBtn, setShowNorthBtn] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const navigate = useNavigate();

  // Ride review (detail screen) drives the map through context: a track to draw + the
  // scrub-cursor index. Null track means we're on the map screen.
  const { track: reviewTrack, scrubIndex, metric } = useRideReview();
  const { recordPosition } = useRideRecorder();

  // Route planner (/route) drives the map the same way: a planned/GPX route to draw.
  // We mirror the latest fix into its positionRef so the planner has a start point.
  const { plannedRoute, positionRef: planPositionRef } = useRoutePlan();

  // Route editor (M3): the map is the editing surface — tap to add waypoints, tap a
  // line to select a segment, drag a waypoint disc to move it. Whole-context ref so the
  // pointer handlers (registered once per editing session) always read fresh state.
  const editor = useRouteEditor();
  const editorRef = useRef(editor);
  editorRef.current = editor;
  const editorWasActive = useRef(false);

  // Discover (M4): draw the search results' overview lines, mirror the map center for
  // "Search this area", and honour place-search jumps.
  const discover = useDiscover();
  // Mirror for the review effect, which refits to the route on review close without
  // wanting to re-run whenever a new route is planned.
  const plannedRouteRef = useRef(plannedRoute);
  plannedRouteRef.current = plannedRoute;

  const { settings } = useSettings();
  const zoomBase = zoomBaseFromOffset(settings.zoomOffset);

  // Keep the screen awake while the app is up — auto-lock kills the glanceable map,
  // watchPosition and recording all at once. Held for the map's whole lifetime (it's a
  // riding app first); revisit with a setting if desk-browsing battery ever matters.
  useWakeLock(true);

  // Mirrors for the GPS effect, which reads these every tick but shouldn't re-run (and
  // hit its unchanged-coordinate early return) just because a toggle flipped. Dedicated
  // effects below apply each toggle immediately so the map still feels responsive.
  const speedZoomRef = useRef(settings.speedZoom);
  speedZoomRef.current = settings.speedZoom;
  const lockNorthRef = useRef(settings.lockNorth);
  lockNorthRef.current = settings.lockNorth;
  const showPeersRef = useRef(settings.showPeers);
  showPeersRef.current = settings.showPeers;

  const { position, error: gpsError } = useGps();
  // Where the rider is *along* the loaded route (matching, off-route detection, ETA).
  // Drives the anchored line emphasis below and the progress readout in the bottom bar.
  const routeProgress = useRouteProgress(plannedRoute, position);
  const prevPosition = useRef<{ lat: number; lon: number } | null>(null);
  // First auto-centre snaps (jumpTo) rather than animating across the globe from the
  // initial view; subsequent follows ease smoothly.
  const hasCentered = useRef(false);
  const lastPosSaveTime = useRef(0);

  // Phase 5 — feed our position to the voice channel and draw the other riders.
  const { peerLocations, reportPosition } = useVoice();
  // Mirror for the staleness sweep interval, so it doesn't restart on every update.
  const peerLocationsRef = useRef(peerLocations);
  peerLocationsRef.current = peerLocations;

  // Derived from position — no separate state needed
  const speedKph = position?.speed != null ? Math.round(position.speed * 3.6) : 0;
  // Mirror for effects that need the current speed without re-running on every change
  const speedKphRef = useRef(speedKph);
  speedKphRef.current = speedKph;

  // Phase 3 state — cameras update map sources directly (refs); speed limit drives BottomBar (state)
  const [speedLimit, setSpeedLimit] = useState<number | null>(null);
  const localCameras = useRef<CameraSet>(EMPTY_CAMERA_SET);
  const routeCameras = useRef<CameraSet>(EMPTY_CAMERA_SET);
  const cameraFetchCenter = useRef<{ lat: number; lon: number } | null>(null);
  const speedLimitWays = useRef<SpeedLimitWay[]>([]);
  const speedLimitFetchCenter = useRef<{ lat: number; lon: number } | null>(null);
  const speedLimitRetryAt = useRef(0); // no refetch before this after a failure
  // Independent abort channels — these fetches can all run concurrently
  const localCameraAbort = useRef<AbortController | null>(null);
  const routeCameraAbort = useRef<AbortController | null>(null);
  const speedLimitAbort = useRef<AbortController | null>(null);

  function isAbort(e: unknown): boolean {
    return e instanceof DOMException && e.name === 'AbortError';
  }

  function writeCameras() {
    const map = mapRef.current;
    if (!map) return;
    const merged = mergeCameraSets(routeCameras.current, localCameras.current);
    const set = (id: string, features: FeatureCollection['features']) =>
      (map.getSource(id) as GeoJSONSource | undefined)?.setData({
        type: 'FeatureCollection',
        features,
      });
    set(SOURCES.CAMERAS_FIXED, merged.fixed);
    set(SOURCES.CAMERAS_AVG_LINE, merged.avgLines);
    set(SOURCES.CAMERAS_AVG_POINT, merged.avgPoints);
  }

  // ── Map initialisation ──────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;

    const last = readLastPos();
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAP_STYLE,
      center: last.center,
      zoom: last.zoom,
      attributionControl: false,
    });

    // dragstart only fires on user-initiated drags, never on programmatic moves
    map.on('dragstart', () => {
      trackingRef.current = false;
      setTracking(false);
      setShowNorthBtn(map.getBearing() !== 0);
    });

    map.on('rotate', () => {
      setShowNorthBtn(!trackingRef.current && map.getBearing() !== 0);
    });

    map.on('load', () => {
      addLayersToMap(map);
      setMapLoaded(true);
    });

    mapRef.current = map;
    return () => {
      localCameraAbort.current?.abort();
      routeCameraAbort.current?.abort();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // ── Group rider dots (Phase 5) ──────────────────────────────────────────
  // Reads through the ref so the same stable callback serves both the data-driven
  // effect and the periodic staleness sweep.
  const writePeers = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const source = map.getSource(SOURCES.PEERS) as GeoJSONSource | undefined;
    if (!source) return;
    if (!showPeersRef.current) {
      source.setData({ type: 'FeatureCollection', features: [] });
      return;
    }
    const now = Date.now();
    source.setData({
      type: 'FeatureCollection',
      features: peerLocationsRef.current.map((p) => {
        // The heading tick only shows when the peer is actually moving — a stationary
        // fix reports a meaningless (often stuck or NaN) heading.
        const kph = p.speed != null && !Number.isNaN(p.speed) ? p.speed * 3.6 : 0;
        const hasHeading = p.heading != null && !Number.isNaN(p.heading) && kph > MOVING_KPH;
        return {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [p.lon, p.lat] },
          properties: {
            name: p.name,
            initials: initials(p.name),
            color: peerColorIndex(p.name),
            heading: hasHeading ? p.heading : 0,
            hasHeading,
            stale: now - p.receivedAt > PEER_STALE_MS,
          },
        };
      }),
    });
  }, []);

  useEffect(() => {
    if (!mapLoaded) return;
    writePeers();
  }, [peerLocations, mapLoaded, writePeers, settings.showPeers]);

  // Staleness is a function of time, not just data — re-evaluate even when no updates
  // arrive, otherwise a rider who drops out keeps their fresh color forever.
  useEffect(() => {
    if (!mapLoaded) return;
    const id = setInterval(writePeers, PEER_STALE_SWEEP_MS);
    return () => clearInterval(id);
  }, [mapLoaded, writePeers]);

  // Share our position with the channel (no-op while not joined). Separate from the map
  // effect below: that one early-returns on unchanged coordinates and an unloaded map,
  // but speed/heading should keep flowing to the group regardless.
  useEffect(() => {
    if (position) {
      reportPosition(position);
      recordPosition(position);
    }
  }, [position, reportPosition, recordPosition]);

  // ── GPS position updates ────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded || !position) return;

    // Skip if coordinates haven't changed
    const prev = prevPosition.current;
    if (prev && prev.lat === position.lat && prev.lon === position.lon) return;
    prevPosition.current = { lat: position.lat, lon: position.lon };

    // Remember roughly where we are for the next cold start (immediately on the first fix,
    // then throttled) — so we never open on the Pacific again.
    const saveNow = Date.now();
    if (!prev || saveNow - lastPosSaveTime.current > LAST_POS_SAVE_MS) {
      lastPosSaveTime.current = saveNow;
      try {
        localStorage.setItem(LAST_POS_KEY, JSON.stringify({ lat: position.lat, lon: position.lon }));
      } catch {
        /* storage unavailable / full — non-fatal */
      }
    }

    const kph = position.speed != null ? Math.round(position.speed * 3.6) : 0;

    // Marker switches between the heading arrow (moving) and the pulsing dot (stationary
    // or no usable heading) via layer filters on `moving`.
    const hasHeading = position.heading != null && !Number.isNaN(position.heading);
    (map.getSource(SOURCES.GPS) as GeoJSONSource).setData({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [position.lon, position.lat] },
      properties: {
        moving: kph > MOVING_KPH && hasHeading,
        heading: hasHeading ? position.heading : 0,
      },
    });

    if (trackingRef.current) {
      const camera = {
        center: [position.lon, position.lat] as [number, number],
        zoom: targetZoom(kph, zoomBase, speedZoomRef.current),
        // North-locked: always face north. Otherwise rotate to heading when moving and
        // keep the current bearing when stationary.
        bearing: lockNorthRef.current
          ? 0
          : position.heading != null && kph > MOVING_KPH
            ? position.heading
            : map.getBearing(),
      };
      // First fix: snap there instantly instead of animating across the globe from the
      // last-known / world view. After that, follow smoothly.
      if (hasCentered.current) {
        map.easeTo(camera);
      } else {
        map.jumpTo(camera);
        hasCentered.current = true;
      }
    }

    // Refresh local cameras when we've drifted past the inner CAMERA_REFRESH_KM boundary
    const center = cameraFetchCenter.current;
    if (!center || haversineKm(position.lat, position.lon, center.lat, center.lon) > CAMERA_REFRESH_KM) {
      cameraFetchCenter.current = { lat: position.lat, lon: position.lon };
      localCameraAbort.current?.abort();
      const ctrl = new AbortController();
      localCameraAbort.current = ctrl;
      fetchCamerasForBbox(bboxFromCenter(position.lat, position.lon, CAMERA_FETCH_KM), ctrl.signal)
        .then((set) => {
          localCameras.current = set;
          writeCameras();
        })
        .catch((e) => {
          if (!isAbort(e)) console.warn('Local camera fetch failed:', e);
        });
    }

    // Prefetch the maxspeed ways around us past the inner refresh boundary. A failure
    // clears the center so the next tick retries, after a backoff; the previous set
    // stays matchable throughout (roads don't move).
    const slCenter = speedLimitFetchCenter.current;
    if (
      (!slCenter || haversineKm(position.lat, position.lon, slCenter.lat, slCenter.lon) > SPEED_LIMIT_REFRESH_KM) &&
      Date.now() >= speedLimitRetryAt.current
    ) {
      speedLimitFetchCenter.current = { lat: position.lat, lon: position.lon };
      speedLimitAbort.current?.abort();
      const ctrl = new AbortController();
      speedLimitAbort.current = ctrl;
      fetchSpeedLimitWays(
        bboxFromCenter(position.lat, position.lon, SPEED_LIMIT_FETCH_KM, SPEED_LIMIT_SNAP_DEG),
        ctrl.signal,
      )
        .then((ways) => {
          speedLimitWays.current = ways;
        })
        .catch((e) => {
          if (isAbort(e)) return;
          console.warn('Speed limit fetch failed:', e);
          speedLimitFetchCenter.current = null;
          speedLimitRetryAt.current = Date.now() + SPEED_LIMIT_RETRY_MS;
        });
    }

    // Match the current road against the cached ways — no network on this path, so the
    // display reacts to a road change on the next tick. Heading only helps while
    // moving; a stationary fix's heading is noise.
    setSpeedLimit(
      matchSpeedLimit(
        speedLimitWays.current,
        position.lat,
        position.lon,
        kph > MOVING_KPH && hasHeading ? position.heading : null,
      ),
    );
  }, [position, mapLoaded, zoomBase]);

  // Live-react to zoom offset changes while tracking, so the slider feels responsive
  // even between GPS ticks. Speed is read from a ref on purpose: with speedKph in the
  // deps this would fire on every GPS tick where the speed changed, and its easeTo
  // would cancel the centering animation the GPS effect just started (easeTo stops any
  // in-flight ease) — making the map stop following while accelerating or braking.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded || !trackingRef.current) return;
    map.easeTo({ zoom: targetZoom(speedKphRef.current, zoomBase, settings.speedZoom) });
  }, [zoomBase, mapLoaded, settings.speedZoom]);

  // Snap to north the moment north-lock is enabled, rather than waiting for the next GPS
  // tick. (Disabling it does nothing now — the next heading update rotates the map.)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded || !settings.lockNorth) return;
    map.easeTo({ bearing: 0 });
    setShowNorthBtn(false);
  }, [settings.lockNorth, mapLoaded]);

  // ── Ride review (detail screen) ───────────────────────────────────────────
  // Enter/exit: fit the visible map area around the track, pause GPS-follow, and hide the
  // live own-marker (it'd sit at the rider's current location, not on the ride). Cleared
  // when the detail page drops the track. Colouring is the separate effect below, so a
  // metric change recolours without re-fitting the view.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;

    const clear = (id: string) =>
      (map.getSource(id) as GeoJSONSource | undefined)?.setData({ type: 'FeatureCollection', features: [] });

    if (!reviewTrack || reviewTrack.length === 0) {
      clear(SOURCES.REVIEW_TRACK);
      clear(SOURCES.REVIEW_CURSOR);
      map.setLayoutProperty('gps-marker-stationary', 'visibility', 'visible');
      map.setLayoutProperty('gps-marker-moving', 'visibility', 'visible');
      // Any planned/GPX route comes back out of hiding (data untouched), and the camera
      // returns to it — review's fitBounds left the view framed on the ride, which can
      // be anywhere. Via ref: null until a route exists, so the initial run never fits.
      for (const id of ROUTE_LAYER_IDS) map.setLayoutProperty(id, 'visibility', 'visible');
      const route = plannedRouteRef.current;
      if (route) {
        const routeBounds = boundsFromGeoJSON(route);
        if (!routeBounds.isEmpty()) map.fitBounds(routeBounds, { padding: 40, duration: 600 });
      }
      return;
    }

    map.setLayoutProperty('gps-marker-stationary', 'visibility', 'none');
    map.setLayoutProperty('gps-marker-moving', 'visibility', 'none');
    // Hide the planned/GPX route under review — two coloured lines stacking is unreadable.
    for (const id of ROUTE_LAYER_IDS) map.setLayoutProperty(id, 'visibility', 'none');
    trackingRef.current = false;
    setTracking(false);
    setShowNorthBtn(false);

    const bounds = new maplibregl.LngLatBounds();
    for (const p of reviewTrack) bounds.extend([p.lon, p.lat]);
    if (!bounds.isEmpty()) {
      // Keep the track clear of the floating header and the stats/charts panel (bottom in
      // portrait, right on wide/landscape screens) — mirrors the RideDetailPage layout.
      map.fitBounds(bounds, { padding: panelAwarePadding(80, 0.58), duration: 600 });
    }
  }, [reviewTrack, mapLoaded]);

  // Colour the review track by the active metric (segment values + matching ramp).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded || !reviewTrack || reviewTrack.length === 0) return;
    (map.getSource(SOURCES.REVIEW_TRACK) as GeoJSONSource | undefined)?.setData(trackToSegments(reviewTrack, metric));
    map.setPaintProperty('review-track', 'line-color', rampExpression(metric));
  }, [reviewTrack, metric, mapLoaded]);

  // Move the scrub cursor to the point at scrubIndex (or hide it).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    const cursor = map.getSource(SOURCES.REVIEW_CURSOR) as GeoJSONSource | undefined;
    if (!cursor) return;

    if (!reviewTrack || scrubIndex == null || scrubIndex < 0 || scrubIndex >= reviewTrack.length) {
      cursor.setData({ type: 'FeatureCollection', features: [] });
      return;
    }
    const p = reviewTrack[scrubIndex];
    cursor.setData({ type: 'Feature', geometry: { type: 'Point', coordinates: [p.lon, p.lat] }, properties: {} });
  }, [scrubIndex, reviewTrack, mapLoaded]);

  // ── Route editor (M3) ─────────────────────────────────────────────────────
  // Enter/exit: hide the planned/GPX route layers (the editor draws its own lines), stop
  // GPS-follow, and — when reopening a saved route — fit the view around it. Reads the
  // hydrated waypoints/segments through the ref: activation and hydration land in one
  // state update, so they're in place when `active` flips.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;

    if (editor.active) {
      for (const id of ROUTE_LAYER_IDS) map.setLayoutProperty(id, 'visibility', 'none');
      trackingRef.current = false;
      setTracking(false);
      setShowNorthBtn(false);
      const { waypoints, segments } = editorRef.current;
      if (!editorWasActive.current && waypoints.length > 0) {
        const bounds = new maplibregl.LngLatBounds();
        for (const s of segments) for (const c of s.coords) bounds.extend(c);
        for (const w of waypoints) bounds.extend([w.lon, w.lat]);
        if (!bounds.isEmpty()) {
          // Keep clear of the floating header and the editor panel (bottom on phones,
          // right column on wide screens).
          map.fitBounds(bounds, { padding: panelAwarePadding(90, 0.36), duration: 600 });
        }
      }
      editorWasActive.current = true;
    } else if (editorWasActive.current) {
      editorWasActive.current = false;
      for (const id of ROUTE_LAYER_IDS) map.setLayoutProperty(id, 'visibility', 'visible');
    }
  }, [editor.active, mapLoaded]);

  // Mirror the editor model into the map sources. Settled segments draw as route lines —
  // chunked so the route-blue gradient can run start→finish across features (straight ones
  // via the dashed layer). Failed segments go into the same *selectable* source as red
  // dashed beelines (tap to retry / change mode); only still-routing segments remain grey
  // draft ghosts.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    const setData = (id: string, features: Feature[]) =>
      (map.getSource(id) as GeoJSONSource | undefined)?.setData({ type: 'FeatureCollection', features });

    if (!editor.active) {
      setData(SOURCES.EDITOR_LINE, []);
      setData(SOURCES.EDITOR_WAYPOINTS, []);
      setData(SOURCES.EDITOR_DRAFT, []);
      return;
    }

    const selection = editor.selection;
    const lines: Feature[] = [];
    const drafts: Feature[] = [];
    const totalMeters = editor.segments.reduce((sum, s) => sum + s.distanceMeters, 0) || 1;
    let cumMeters = 0;
    for (const segment of editor.segments) {
      const selected = selection?.kind === 'segment' && selection.id === segment.id;
      if (segment.status === 'routing') {
        drafts.push({
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: segment.coords },
          properties: { id: segment.id },
        });
      } else if (segment.status === 'error') {
        lines.push({
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: segment.coords },
          properties: { id: segment.id, failed: true, selected },
        });
      } else {
        // Split into chunks, each a solid colour sampled where its midpoint sits along
        // the whole route — a stepped approximation of the planned-route line-gradient.
        const lastVertex = segment.coords.length - 1;
        const share = Math.round(EDITOR_GRAD_CHUNKS * (segment.distanceMeters / totalMeters));
        const chunks = Math.max(1, Math.min(share, lastVertex));
        for (let c = 0; c < chunks; c++) {
          const a = Math.round((c * lastVertex) / chunks);
          const b = Math.round(((c + 1) * lastVertex) / chunks);
          if (b <= a) continue;
          const fraction = (cumMeters + segment.distanceMeters * ((c + 0.5) / chunks)) / totalMeters;
          lines.push({
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: segment.coords.slice(a, b + 1) },
            properties: {
              id: segment.id,
              straight: segment.mode === 'straight',
              selected,
              color: editorGradColor(fraction),
            },
          });
        }
      }
      cumMeters += segment.distanceMeters;
    }
    setData(SOURCES.EDITOR_LINE, lines);
    setData(SOURCES.EDITOR_DRAFT, drafts);
    setData(
      SOURCES.EDITOR_WAYPOINTS,
      editor.waypoints.map((w, i) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [w.lon, w.lat] },
        properties: {
          id: w.id,
          label: String(i + 1),
          selected: selection?.kind === 'waypoint' && selection.id === w.id,
        },
      })),
    );
  }, [editor.active, editor.waypoints, editor.segments, editor.selection, mapLoaded]);

  // Pointer wiring, live only while editing. Tap = add waypoint at the end; tap a
  // waypoint/segment (12 px padded hit box, waypoints win) = select it; drag a waypoint =
  // dashed elastic ghost to its neighbours, one re-route per touched segment on release.
  // Drag the line *itself* = grab a fresh "via here" at the finger and re-route — the
  // Google-Maps gesture, so you don't have to select the segment then tap "+ Via here".
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded || !editor.active) return;

    const pad = 12;
    // A tap over a line jitters a few px on touch; only past this is it a drag, not a select.
    const DRAG_THRESHOLD = 6;
    const queryAt = (p: { x: number; y: number }, layers: string[]) =>
      map.queryRenderedFeatures(
        [
          [p.x - pad, p.y - pad],
          [p.x + pad, p.y + pad],
        ],
        { layers },
      );

    const ghost = (from: [number, number], to: [number, number]): Feature => ({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: [from, to] },
      properties: {},
    });
    const setDraft = (features: Feature[]) =>
      (map.getSource(SOURCES.EDITOR_DRAFT) as GeoJSONSource | undefined)?.setData({
        type: 'FeatureCollection',
        features,
      });

    let dragId: string | null = null;
    let dragMoved = false;
    let suppressClick = false;

    // ── Waypoint drag: move an existing numbered disc ─────────────────────────
    const writeWaypointGhost = (id: string, lngLat: maplibregl.LngLat) => {
      const { waypoints } = editorRef.current;
      const i = waypoints.findIndex((w) => w.id === id);
      if (i < 0) return;
      const features: Feature[] = [];
      if (i > 0) features.push(ghost([waypoints[i - 1].lon, waypoints[i - 1].lat], [lngLat.lng, lngLat.lat]));
      if (i < waypoints.length - 1) features.push(ghost([lngLat.lng, lngLat.lat], [waypoints[i + 1].lon, waypoints[i + 1].lat]));
      setDraft(features);
    };

    const onMove = (e: maplibregl.MapMouseEvent | maplibregl.MapTouchEvent) => {
      if (!dragId) return;
      dragMoved = true;
      writeWaypointGhost(dragId, e.lngLat);
    };

    const endDrag = (e: maplibregl.MapMouseEvent | maplibregl.MapTouchEvent) => {
      if (!dragId) return;
      const id = dragId;
      dragId = null;
      map.off('mousemove', onMove);
      map.off('touchmove', onMove);
      // Remove the paired once() that didn't fire: on a touch-only device only touchend
      // fires, leaving the mouseup once() registered and accumulating one per drag.
      map.off('mouseup', endDrag);
      map.off('touchend', endDrag);
      map.dragPan.enable();
      if (dragMoved) {
        // The click that follows mouseup would otherwise re-select or add a waypoint.
        suppressClick = true;
        editorRef.current.moveWaypoint(id, e.lngLat.lat, e.lngLat.lng);
      }
    };

    const onDown = (e: maplibregl.MapLayerMouseEvent | maplibregl.MapLayerTouchEvent) => {
      if ('points' in e && e.points.length > 1) return; // pinch — let the map zoom
      const id = e.features?.[0]?.properties?.id;
      if (id == null) return;
      e.preventDefault();
      dragId = String(id);
      dragMoved = false;
      map.dragPan.disable();
      map.on('mousemove', onMove);
      map.on('touchmove', onMove);
      map.once('mouseup', endDrag);
      map.once('touchend', endDrag);
    };

    // ── Line drag: pull a segment to drop a new "via here" where the finger lands ──
    // Holding still on the line (~long-press) inserts the via in place instead — dragging
    // a route you *don't* want to change felt wrong as the only gesture.
    const LONG_PRESS_MS = 450;
    let viaSegId: string | null = null;
    let viaDown: { x: number; y: number } | null = null;
    let pressTimer: number | null = null;

    const clearPressTimer = () => {
      if (pressTimer != null) {
        clearTimeout(pressTimer);
        pressTimer = null;
      }
    };

    const writeViaGhost = (segId: string, lngLat: maplibregl.LngLat) => {
      const { waypoints, segments } = editorRef.current;
      const si = segments.findIndex((s) => s.id === segId);
      if (si < 0) return;
      const from = waypoints[si];
      const to = waypoints[si + 1];
      setDraft([
        ghost([from.lon, from.lat], [lngLat.lng, lngLat.lat]),
        ghost([lngLat.lng, lngLat.lat], [to.lon, to.lat]),
      ]);
    };

    const onViaMove = (e: maplibregl.MapMouseEvent | maplibregl.MapTouchEvent) => {
      if (!viaSegId || !viaDown) return;
      if (!dragMoved && Math.hypot(e.point.x - viaDown.x, e.point.y - viaDown.y) < DRAG_THRESHOLD) return;
      clearPressTimer(); // moved past the jitter box — it's a drag, not a hold
      dragMoved = true;
      writeViaGhost(viaSegId, e.lngLat);
    };

    const endViaDrag = (e: maplibregl.MapMouseEvent | maplibregl.MapTouchEvent) => {
      clearPressTimer();
      if (!viaSegId) return;
      const segId = viaSegId;
      viaSegId = null;
      viaDown = null;
      map.off('mousemove', onViaMove);
      map.off('touchmove', onViaMove);
      // Remove the paired once() that didn't fire (touch-only devices — see endDrag).
      map.off('mouseup', endViaDrag);
      map.off('touchend', endViaDrag);
      map.dragPan.enable();
      if (dragMoved) {
        suppressClick = true; // don't let the trailing click re-select/add
        editorRef.current.insertViaAt(segId, e.lngLat.lat, e.lngLat.lng);
      } else {
        setDraft([]); // stray ghost from a near-still grab; the click falls through to select
      }
    };

    const onLineDown = (e: maplibregl.MapLayerMouseEvent | maplibregl.MapLayerTouchEvent) => {
      if ('points' in e && e.points.length > 1) return; // pinch — let the map zoom
      if (dragId || viaSegId) return; // a drag already owns this gesture
      // Waypoints win: if a disc is under the finger, its own handler takes the drag.
      if (queryAt(e.point, [EDITOR_WAYPOINT_LAYER_ID]).length > 0) return;
      const id = e.features?.[0]?.properties?.id;
      if (id == null) return;
      e.preventDefault();
      viaSegId = String(id);
      viaDown = { x: e.point.x, y: e.point.y };
      dragMoved = false;
      map.dragPan.disable();
      map.on('mousemove', onViaMove);
      map.on('touchmove', onViaMove);
      map.once('mouseup', endViaDrag);
      map.once('touchend', endViaDrag);
      // Held still long enough → insert the via right where the press landed. The gesture
      // is consumed here; the eventual mouseup/touchend finds viaSegId null and no-ops.
      const downLngLat = e.lngLat;
      clearPressTimer();
      pressTimer = window.setTimeout(() => {
        pressTimer = null;
        if (!viaSegId || dragMoved) return;
        const segId = viaSegId;
        viaSegId = null;
        viaDown = null;
        map.off('mousemove', onViaMove);
        map.off('touchmove', onViaMove);
        map.dragPan.enable();
        suppressClick = true;
        navigator.vibrate?.(30);
        editorRef.current.insertViaAt(segId, downLngLat.lat, downLngLat.lng);
      }, LONG_PRESS_MS);
    };

    const onClick = (e: maplibregl.MapMouseEvent) => {
      if (suppressClick) {
        suppressClick = false;
        return;
      }
      const wp = queryAt(e.point, [EDITOR_WAYPOINT_LAYER_ID]);
      if (wp.length > 0) {
        editorRef.current.select({ kind: 'waypoint', id: String(wp[0].properties?.id) });
        return;
      }
      const seg = queryAt(e.point, [...EDITOR_LINE_LAYER_IDS]);
      if (seg.length > 0) {
        editorRef.current.select({ kind: 'segment', id: String(seg[0].properties?.id) });
        return;
      }
      editorRef.current.addWaypoint(e.lngLat.lat, e.lngLat.lng);
    };

    map.on('click', onClick);
    map.on('mousedown', EDITOR_WAYPOINT_LAYER_ID, onDown);
    map.on('touchstart', EDITOR_WAYPOINT_LAYER_ID, onDown);
    for (const layerId of EDITOR_LINE_LAYER_IDS) {
      map.on('mousedown', layerId, onLineDown);
      map.on('touchstart', layerId, onLineDown);
    }
    return () => {
      map.off('click', onClick);
      map.off('mousedown', EDITOR_WAYPOINT_LAYER_ID, onDown);
      map.off('touchstart', EDITOR_WAYPOINT_LAYER_ID, onDown);
      for (const layerId of EDITOR_LINE_LAYER_IDS) {
        map.off('mousedown', layerId, onLineDown);
        map.off('touchstart', layerId, onLineDown);
      }
      map.off('mousemove', onMove);
      map.off('touchmove', onMove);
      map.off('mouseup', endDrag);
      map.off('touchend', endDrag);
      map.off('mousemove', onViaMove);
      map.off('touchmove', onViaMove);
      map.off('mouseup', endViaDrag);
      map.off('touchend', endViaDrag);
      clearPressTimer();
      map.dragPan.enable();
    };
  }, [editor.active, mapLoaded]);

  // ── Route progress (anchored emphasis) ────────────────────────────────────
  // Static half: the loaded route pre-chunked into ~150 m features carrying their
  // cumulative distance `s` — computed once per route, filtered/coloured live below.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    const source = map.getSource(SOURCES.ROUTE_PROGRESS) as GeoJSONSource | undefined;
    if (!source) return;
    const coords = plannedRoute ? extractRouteLine(plannedRoute) : [];
    source.setData({
      type: 'FeatureCollection',
      features: coords.length >= 2 ? buildProgressChunks(coords, cumulativeMeters(coords)) : [],
    });
  }, [plannedRoute, mapLoaded]);

  // Live half: while locked onto the route, swap the plain gradient line for the
  // emphasis pair — base repainted ridden-grey/ahead-muted around the rider's progress,
  // the near window refiltered vivid (and, being the later layer, drawn on top — on a
  // road the route uses twice the nearer pass wins both contrast and stacking). Runs
  // after the review/editor effects, whose exit paths flip every route layer visible,
  // so this always gets the last word on which line is showing. Throttled to
  // PROGRESS_STEP_M progress steps via the bucket dep.
  const progressBucket = Math.floor(routeProgress.sMeters / PROGRESS_STEP_M);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    const setVis = (id: string, visible: boolean) =>
      map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');

    const suppressed = (reviewTrack !== null && reviewTrack.length > 0) || editor.active;
    if (suppressed || routeProgress.status !== 'onRoute') {
      for (const id of ROUTE_PROGRESS_LAYER_IDS) setVis(id, false);
      // While review/editor are up they own the route layers' visibility; otherwise
      // hand the route back to the plain gradient line.
      if (!suppressed) setVis('route-line', true);
      return;
    }

    const s = routeProgress.sMeters;
    map.setPaintProperty('route-progress-base', 'line-color', [
      'case',
      ['<', ['get', 's'], s],
      PROGRESS_RIDDEN_COLOR,
      PROGRESS_AHEAD_COLOR,
    ]);
    map.setFilter('route-progress-near', [
      'all',
      ['>=', ['get', 's'], s - PROGRESS_NEAR_BACK_M],
      ['<=', ['get', 's'], s + PROGRESS_NEAR_FWD_M],
    ]);
    setVis('route-line', false);
    for (const id of ROUTE_PROGRESS_LAYER_IDS) setVis(id, true);
    // sMeters is read through progressBucket on purpose — repaint on 50 m steps, not per fix.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapLoaded, routeProgress.status, progressBucket, reviewTrack, editor.active, plannedRoute]);

  // ── Discover (M4) ─────────────────────────────────────────────────────────
  // Keep the discover screen supplied with the current map center (ref write — no
  // re-renders); "Search this area" reads it at search time.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    const write = () => {
      const c = map.getCenter();
      discover.mapCenterRef.current = { lat: c.lat, lon: c.lng };
    };
    write();
    map.on('moveend', write);
    return () => {
      map.off('moveend', write);
    };
  }, [mapLoaded, discover.mapCenterRef]);

  // Result lines: every hit's overview polyline, the focused one highlighted.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    const source = map.getSource(SOURCES.DISCOVER_ROUTES) as GeoJSONSource | undefined;
    if (!source) return;

    if (!discover.active || !discover.results) {
      source.setData({ type: 'FeatureCollection', features: [] });
      return;
    }
    source.setData({
      type: 'FeatureCollection',
      features: discover.results.map((r) => ({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: decodePolyline(r.overviewPolyline).map(([lat, lon]) => [lon, lat] as [number, number]),
        },
        properties: { id: r.id, focused: r.id === discover.focusedId },
      })),
    });
  }, [discover.active, discover.results, discover.focusedId, mapLoaded]);

  // Focusing a result frames it (panel occupies the lower part of the screen).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded || !discover.focusedId) return;
    const hit = discover.results?.find((r) => r.id === discover.focusedId);
    if (!hit) return;
    const bounds = new maplibregl.LngLatBounds();
    for (const [lat, lon] of decodePolyline(hit.overviewPolyline)) bounds.extend([lon, lat]);
    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, { padding: panelAwarePadding(80, 0.42), duration: 600 });
    }
  }, [discover.focusedId, discover.results, mapLoaded]);

  // Place-search jump: ease anywhere in the world, pausing GPS-follow. Discover browses
  // an area (zoom 10); the editor jumps to a searched waypoint and asks for a closer look.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded || !discover.flyTarget) return;
    trackingRef.current = false;
    setTracking(false);
    map.easeTo({
      center: [discover.flyTarget.lon, discover.flyTarget.lat],
      zoom: discover.flyTarget.zoom ?? 10,
      duration: 800,
    });
  }, [discover.flyTarget, mapLoaded]);

  // Tapping a result line on the map focuses it — same first-tap/second-tap ladder as the
  // list cards: focus frames it, a tap on the already-focused line opens its page.
  const { setFocusedId: focusDiscoverRoute } = discover;
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded || !discover.active) return;
    const pad = 8;
    const focusedId = discover.focusedId;
    const onClick = (e: maplibregl.MapMouseEvent) => {
      const hits = map.queryRenderedFeatures(
        [
          [e.point.x - pad, e.point.y - pad],
          [e.point.x + pad, e.point.y + pad],
        ],
        { layers: [DISCOVER_ROUTES_LAYER_ID] },
      );
      const id = hits[0]?.properties?.id;
      if (id == null) return;
      if (String(id) === focusedId) {
        navigate(`/routes/${String(id)}`);
      } else {
        focusDiscoverRoute(String(id));
      }
    };
    map.on('click', onClick);
    return () => {
      map.off('click', onClick);
    };
  }, [discover.active, discover.focusedId, focusDiscoverRoute, mapLoaded, navigate]);

  // ── Control handlers ────────────────────────────────────────────────────
  function handleTrack() {
    const map = mapRef.current;
    if (!map || !position) return;
    trackingRef.current = true;
    setTracking(true);
    setShowNorthBtn(false);
    map.easeTo({
      center: [position.lon, position.lat],
      zoom: targetZoom(speedKph, zoomBase, settings.speedZoom),
      bearing: 0,
    });
  }

  function handleResetNorth() {
    mapRef.current?.resetNorth();
    setShowNorthBtn(false);
  }

  function handleRouteLoad(geojson: FeatureCollection) {
    const map = mapRef.current;
    if (!map) return;
    (map.getSource(SOURCES.ROUTE) as GeoJSONSource).setData(geojson);
    const bounds = boundsFromGeoJSON(geojson);
    if (bounds.isEmpty()) return;

    // On short landscape the planner sheet docks right — keep the fitted route clear of
    // it while the sheet is up (path /route). Elsewhere the historical uniform padding:
    // in portrait the route sits partly behind the bottom sheet until dismissed (accepted),
    // and fits triggered with no sheet open ("Show on map") should use the full viewport.
    const plannerDockedRight =
      window.location.pathname === '/route' && window.matchMedia(SHORT_LANDSCAPE_QUERY).matches;
    map.fitBounds(bounds, {
      padding: plannerDockedRight ? { top: 40, right: dockedPanelWidth() + 40, bottom: 40, left: 40 } : 40,
    });
    trackingRef.current = false;
    setTracking(false);
    setShowNorthBtn(false);

    // Prefetch cameras for the whole route bounds; merged with local cameras at write time
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    routeCameraAbort.current?.abort();
    const ctrl = new AbortController();
    routeCameraAbort.current = ctrl;
    fetchCamerasForBbox({ south: sw.lat, west: sw.lng, north: ne.lat, east: ne.lng }, ctrl.signal)
      .then((set) => {
        routeCameras.current = set;
        writeCameras();
      })
      .catch((e) => {
        if (!isAbort(e)) console.warn('Route camera fetch failed:', e);
      });
  }

  // Keep the route planner supplied with a start point (ref write — no re-renders).
  useEffect(() => {
    planPositionRef.current = position;
  }, [position, planPositionRef]);

  // Draw whatever the planner produced (computed route or parsed GPX) — the same path
  // the old BottomBar GPX input took. Null means cleared: empty the source and drop
  // the route's prefetched cameras (local ones stay). handleRouteLoad is stable in
  // everything but identity, so plannedRoute/mapLoaded are the real triggers.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    if (plannedRoute) {
      handleRouteLoad(plannedRoute);
    } else {
      (map.getSource(SOURCES.ROUTE) as GeoJSONSource).setData({ type: 'FeatureCollection', features: [] });
      routeCameraAbort.current?.abort();
      routeCameras.current = EMPTY_CAMERA_SET;
      writeCameras();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plannedRoute, mapLoaded]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100dvh' }}>
      <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />
      {gpsError && (
        <div
          style={{
            position: 'absolute',
            top: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            maxWidth: '90%',
            background: 'rgba(213, 43, 30, 0.92)',
            color: '#fff',
            padding: '8px 14px',
            borderRadius: 6,
            fontSize: '0.9em',
            zIndex: 15,
            textAlign: 'center',
          }}
        >
          {gpsError}
        </div>
      )}
      {/* Ride telemetry (G-G / lean) — pulls down over the map from the top */}
      <TelemetryDrawer position={position} />
      <BottomBar
        tracking={tracking}
        showNorthBtn={showNorthBtn}
        hasFix={!!position}
        speedKph={speedKph}
        speedLimit={speedLimit}
        speedUnit={settings.speedUnit}
        progress={
          routeProgress.status === 'onRoute'
            ? {
                fraction: routeProgress.fraction,
                remainingMeters: routeProgress.remainingMeters,
                etaSeconds: routeProgress.etaSeconds,
              }
            : null
        }
        onTrack={handleTrack}
        onResetNorth={handleResetNorth}
        onOpenRoute={() => navigate('/route')}
        onOpenRides={() => navigate('/rides')}
        onOpenRoutes={() => navigate('/routes')}
        onOpenDiscover={() => navigate('/discover')}
        onOpenSettings={() => setSettingsOpen(true)}
      />
      {settingsOpen && <SettingsModal onClose={() => setSettingsOpen(false)} />}
    </div>
  );
};

export default MapView;
